import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EvidenceReference, LearnerProfile, PracticeRecord, RubricDimension } from "@pushback-and-chaos/contracts";
import { ARRIVAL, CASEBOOK, LIBRARY, PROPOSED, RECORD } from "../design/copy.js";
import { apiClient } from "../data/transport.js";
import { bookDate, endingByline } from "./storyteller/casebook-atoms.js";
import { arrivalBookQuery } from "./arrival-book.js";
import { Lettering, Pill } from "../ui/primitives.js";
import { EvidenceLinks } from "../ui/evidence-links.js";
import { FreeTextInput } from "../ui/input.js";
import { PanelHeading } from "./storyteller/chrome.js";

/**
 * THE PRACTICE RECORD — the one place a learner returns to between cases.
 * Merges the old Profile and Coach screens, and carries the progression layer:
 * engagement history, the demonstrated repertoire, feedback over time, the
 * shape paragraph, revealed next steps, the daily reflection, and the coach.
 *
 * DRAWN AS ONE SHEET (2026-09-22): the play surface's composition with the
 * record where the story goes and the coach where the desk goes —
 *
 *   +-------------------+-------------------------+
 *   |                   |                         |
 *   |   THE RECORD      |    ASK ABOUT THE RECORD |
 *   |                   |     [reply composer]    |
 *   +-------------------+-------------------------+
 *            57%                     43%
 *
 * — every section under the same small tracked label over a hairline that
 * heads the panels everywhere else. The record's column scrolls (it is a
 * ledger, and a ledger is read down); the coach's conversation sits beside it
 * with its composer at the foot, as on the front door.
 *
 * IN SUMMARY leads: the coach's reading of the whole record — what is going
 * well, what is improving, what to work on next — written by the coach's
 * opening prompt over the same immutable record and cached against it
 * (GET /coach/opening). It is the one prompt that already reads all of the
 * record, so the summary is that prose, unchanged and unrepeated.
 *
 * WHAT THIS SURFACE REFUSES TO RENDER, by design and by test
 * (record.no-rank.test.tsx): a rank. No percentile, no position, no
 * leaderboard, no cohort count — and no numerals at all on the skill surfaces:
 * trends draw as unlabelled lines, standing is words, comparison is SHAPE
 * ("your judgment runs ahead of your quantitative work"), which is
 * strengths-based and structurally incapable of placing anyone above anyone.
 *
 * The trend guard is load-bearing: under three completed cases there is no
 * trend line at all, because two points shown as a direction would be the most
 * confidently wrong thing this surface could say.
 */

/** AMBIGUITY_TOLERANCE -> "ambiguity tolerance" — the one sanctioned rendering of a dimension name. */
function humanize(dimension: RubricDimension): string {
  return dimension.replaceAll("_", " ").toLowerCase();
}

/** An unlabelled sparkline: direction without numbers. Tokens only; 1-5 domain fixed so lines are comparable across dimensions. */
function Sparkline({ points }: { points: readonly number[] }): ReactNode {
  const width = 120;
  const height = 28;
  const step = points.length > 1 ? width / (points.length - 1) : 0;
  const y = (v: number): number => height - ((v - 1) / 4) * (height - 4) - 2;
  const path = points.map((v, i) => `${i === 0 ? "M" : "L"}${String(i * step)},${String(y(v))}`).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${String(width)} ${String(height)}`} aria-hidden="true" className="shrink-0">
      <path d={path} fill="none" stroke="var(--pc-ink-soft)" strokeWidth="1.5" />
      {points.map((v, i) => (
        <circle key={String(i)} cx={i * step} cy={y(v)} r="2" fill="var(--pc-ink)" />
      ))}
    </svg>
  );
}

/**
 * How the learner works, honestly labelled (onboarding pass, 2026-09-13):
 * nothing until there are words behind it, a first guess until an engagement
 * has shown it, and only then a tendency.
 */
export function approachesSentence(approaches: readonly string[], provisional: boolean): string {
  const words = approaches.map((a) => a.replaceAll("_", " ").toLowerCase()).join(" and ");
  if (approaches.length === 0) return "How you work is being learned from your engagements, not from a paragraph.";
  if (provisional) return `You may tend to work ${words}. A first guess from your own words; the engagements will say whether it holds.`;
  return `Tends to work ${words}. That shapes how hard a case pushes, never what you are allowed to try.`;
}

/**
 * THE ARRIVAL ROW (2026-10-11): the record's first book, how the learner met
 * AJ, above the engagements and there before any is finished. The book is
 * kept by the coach's half of the house and asked for here by itself: it
 * exists only while the intake it was assembled from does (404 before the
 * read-back is confirmed, and again once the coach's data is removed), so an
 * absence hides the row rather than reporting anything. Asked once, never
 * retried; mounted only where the page owner can open the book, so a record
 * drawn without that door asks nothing.
 */
function ArrivalRow({ onRead }: { readonly onRead: () => void }): ReactNode {
  const arrival = useQuery(arrivalBookQuery(apiClient()));
  // An error after an answer still hides the row (2026-10-11): TanStack keeps
  // the last data beside the error, and a book the server has since refused
  // (removed with the coach's data) must not go on being offered.
  if (arrival.isError || arrival.data === undefined) return null;
  return (
    <ul className="m-0 list-none p-0 font-sans text-body" data-testid="arrival-row">
      <li className="pc-record-book">
        <button type="button" className="pc-record-row pc-record-book-row" onClick={onRead}>
          <Lettering className="pc-record-book-title">{ARRIVAL.rowTitle}</Lettering>
          <span className="pc-record-book-end">
            <span className="pcs-quiet">{bookDate(arrival.data.metAt)}</span>
            <span>{ARRIVAL.readTheBook}</span>
          </span>
        </button>
      </li>
    </ul>
  );
}

/** One section of the ledger: the banner, then whatever it holds. */
function Section({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }): ReactNode {
  return (
    <section className="pc-record-section" aria-label={label}>
      <PanelHeading label={label}>{aside}</PanelHeading>
      <div className="pc-record-section-body">{children}</div>
    </section>
  );
}

export interface PracticeRecordScreenProps {
  readonly profile: LearnerProfile | null;
  readonly record: PracticeRecord | null;
  readonly coachMessages: readonly { from: "you" | "coach"; text: string; references?: readonly EvidenceReference[] | undefined }[];
  /** "View the moment" (C05): opens the debrief at a moment the coach pointed at. */
  readonly onViewMoment?: ((reference: EvidenceReference) => void) | undefined;
  readonly coachBusy: boolean;
  readonly coachDisabled: boolean;
  readonly onSendCoach: (text: string) => void;
  readonly onSubmitReflection: (answer: string) => void;
  readonly reflectionBusy: boolean;
  readonly onOpenSettings: () => void;
  /**
   * Whether the learner has read their first development plan — the second
   * half of the library's one gate (library pass, 2026-09-08). Null while
   * unknown; the control is hidden until it is.
   */
  readonly planAcknowledged?: boolean | null;
  readonly onAcknowledgePlan?: (() => void) | undefined;
  /** The six skills, explained — one door, from the record, for the learner who wonders what a word means. */
  readonly onOpenSkills?: (() => void) | undefined;
  /** An engagement row opens that engagement's casebook (2026-09-24). */
  readonly onReadBook?: ((sessionId: string) => void) | undefined;
  /**
   * The arrival book's row (2026-10-11), above the engagements: how the
   * learner met AJ. Drawn only where the page owner can open it, and only
   * when the server has it (see `ArrivalRow`).
   */
  readonly onReadArrival?: (() => void) | undefined;
  /**
   * IN SUMMARY (2026-09-22): the coach's reading of the whole record, one to
   * a few paragraphs, from the coach's opening. Null while it is arriving or
   * where there is nobody to read for; `summaryFailed` when it could not be
   * fetched, which the page says in words.
   */
  readonly summary?: readonly string[] | null;
  readonly summaryFailed?: boolean;
}

export function PracticeRecordScreen({
  profile,
  record,
  coachMessages,
  onViewMoment,
  coachBusy,
  coachDisabled,
  onSendCoach,
  onSubmitReflection,
  reflectionBusy,
  onOpenSettings,
  planAcknowledged = null,
  onAcknowledgePlan,
  onOpenSkills,
  onReadBook,
  onReadArrival,
  summary = null,
  summaryFailed = false,
}: PracticeRecordScreenProps): ReactNode {
  const [reflectionDraft, setReflectionDraft] = useState("");
  // The server's answer to "who is this" wins: a returning learner on a fresh
  // browser has no local onboarding state, but their record still knows them.
  const who =
    record?.profile ??
    (profile === null ? null : { name: profile.name, experience_level: profile.experience_level, approaches: [...profile.approaches], approaches_provisional: profile.approaches_provisional });
  const gated = coachDisabled && (record?.profile ?? null) === null;
  const shape = record?.shape ?? null;
  const trends = record?.trends ?? [];
  const showTrends = trends.some((t) => t.sufficient_n);
  const completed = record?.engagement_history.length ?? 0;

  return (
    <main className="pc-sheet pc-record">
      <div className="pc-record-grid">
        {/* THE LEDGER. */}
        <div className="pcs-panel pc-record-ledger" aria-label="Your practice record">
          <div className="pcs-heading">
            <h1 className="pcs-label m-0">{RECORD.title}</h1>
            {onOpenSkills === undefined ? null : (
              <button type="button" onClick={onOpenSkills} className="pcs-quiet cursor-pointer border-0 bg-transparent p-0 hover:bg-marker">
                <Lettering text={RECORD.skillsExplained} />
              </button>
            )}
          </div>

          <div className="pc-record-scroll pc-scroll">
            <Section label={RECORD.summary} aside={summary === null ? undefined : RECORD.summaryFrom}>
              {summary === null ? (
                <p className="pc-record-quiet">{summaryFailed ? RECORD.summaryFailed : gated ? RECORD.summaryGated : RECORD.summaryWaiting}</p>
              ) : (
                summary.map((paragraph) => (
                  <p key={paragraph.slice(0, 40)} className="pc-record-coach-prose">
                    {paragraph}
                  </p>
                ))
              )}
            </Section>

            {/* Who this is — read back as sentences, exactly as it was confirmed (Law 6: inferred, never chosen). */}
            <Section label={RECORD.who}>
              {who === null ? (
                <p className="pc-record-quiet">Nothing extracted yet.</p>
              ) : (
                <div className="font-sans text-lede">
                  <p className="m-0">
                    <strong className="font-display font-bold">{who.name}</strong>,{" "}
                    {who.experience_level.replaceAll("_", " ").toLowerCase()}.
                  </p>
                  <p className="mt-3 mb-0">{approachesSentence(who.approaches, who.approaches_provisional)}</p>
                </div>
              )}
            </Section>

            <Section label={RECORD.engagements}>
              {onReadArrival === undefined ? null : <ArrivalRow onRead={onReadArrival} />}
              {completed === 0 ? (
                <p className="pc-record-quiet">Nothing finished yet — the record starts at your first debrief.</p>
              ) : (
                <ul className="m-0 list-none p-0 font-sans text-body">
                  {record?.engagement_history.map((e) => (
                    <li key={e.session_id} className="pc-record-book">
                      {/* The whole row opens the engagement's book (2026-09-24). */}
                      <button type="button" className="pc-record-row pc-record-book-row" onClick={() => { onReadBook?.(e.session_id); }}>
                        <Lettering className="pc-record-book-title">{e.client}</Lettering>
                        <span className="pc-record-book-end">
                          <span className="pcs-quiet">{endingByline(e.ending, e.completed_at)}</span>
                          <span>{CASEBOOK.readTheBook}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section label={RECORD.techniques}>
              {(record?.techniques.length ?? 0) === 0 ? (
                <p className="pc-record-quiet">The repertoire fills as you show things in live engagements — not as you read about them.</p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {record?.techniques.map((t) => (
                    <li key={t.text.slice(0, 40)} className="pc-record-row pc-record-row-stacked">
                      <p className="m-0 font-sans text-body">{t.text}</p>
                      <p className="pcs-quiet mt-1 mb-0">
                        {t.trains.map(humanize).join(" · ")} — shown at {t.client}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section label={RECORD.feedback}>
              {!showTrends ? (
                <p className="pc-record-quiet">
                  {PROPOSED.notEnoughHistory} Trends appear at three finished engagements — two marks on a page is not a direction, and pretending
                  otherwise would be the most confidently wrong thing this page could do.
                </p>
              ) : (
                <ul className="m-0 list-none p-0">
                  {trends
                    .filter((t) => t.sufficient_n && t.points.length > 1)
                    .map((t) => (
                      <li key={t.dimension} className="pc-record-row">
                        <span className="font-sans text-body">{humanize(t.dimension)}</span>
                        <Sparkline points={t.points} />
                      </li>
                    ))}
                </ul>
              )}
            </Section>

            {shape !== null && shape.leading[0] !== undefined && shape.leading[1] !== undefined ? (
              <Section label={RECORD.shape}>
                <p className="m-0 max-w-[65ch] font-sans text-lede">
                  Your {humanize(shape.leading[0])} and {humanize(shape.leading[1])} run ahead of your {humanize(shape.lagging)} right now. That is a shape,
                  not a standing — and the plan below is chosen for it: most of it aimed at what is behind, some of it keeping what runs ahead in
                  practice.
                </p>
              </Section>
            ) : null}

            {(record?.next_steps.length ?? 0) > 0 ? (
              <Section label={RECORD.nextSteps}>
                <ul className="m-0 list-none p-0">
                  {record?.next_steps.map((step) => (
                    <li key={step.text.slice(0, 40)} className="pc-record-row pc-record-row-stacked">
                      <p className="m-0 font-sans text-body">{step.text}</p>
                      <p className="pcs-quiet mt-1 mb-0">{humanize(step.dimension)}</p>
                    </li>
                  ))}
                </ul>
                <p className="pcs-quiet mt-3 mb-0">Options open up as the record grows. They are ways to practice — nothing here makes a case easier.</p>
                {/* The plan is read once, and saying so is what opens the rest of
                    the library alongside three finished calibration cases. */}
                {planAcknowledged === null || onAcknowledgePlan === undefined ? null : planAcknowledged ? (
                  <p className="mt-4 mb-0 font-sans text-small text-ink-soft">{LIBRARY.planReadDone}</p>
                ) : (
                  <div className="mt-4">
                    <Pill onClick={onAcknowledgePlan}>{LIBRARY.planRead}</Pill>
                  </div>
                )}
              </Section>
            ) : null}

            <Section label={RECORD.reflection}>
              {record === null ? null : record.reflection.answered_today ? (
                <p className="m-0 font-sans text-body text-ink-soft">
                  Written for today.
                  {record.reflection.running_days > 1 ? ` ${String(record.reflection.running_days)} days running.` : ""}
                </p>
              ) : (
                <div>
                  <p className="mt-0 mb-3 max-w-[65ch] font-sans text-body">{record.reflection.question}</p>
                  <FreeTextInput
                    placeholder="Two minutes. Plain words."
                    disabled={reflectionBusy || gated}
                    disabledReason={gated ? "Finish onboarding first." : reflectionBusy ? "Keeping it." : undefined}
                    onSubmit={(text) => {
                      setReflectionDraft("");
                      onSubmitReflection(text);
                    }}
                    onOpenSettings={onOpenSettings}
                    draft={reflectionDraft}
                    onDraftChange={setReflectionDraft}
                    compact
                  />
                  {record.reflection.running_days > 0 ? (
                    <p className="pcs-quiet mt-2 mb-0">
                      {String(record.reflection.running_days)} {record.reflection.running_days === 1 ? "day" : "days"} running — the streak belongs to
                      the reflection, never to the spend.
                    </p>
                  ) : null}
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* THE COACH, beside the ledger: the conversation about it, and the composer at its foot. */}
        <section className="pcs-panel pc-record-coach" aria-label="Ask about the record">
          <PanelHeading label={RECORD.coach}>{PROPOSED.coachStanding}</PanelHeading>
          <div className="pc-record-scroll pc-scroll pc-record-thread">
            {coachMessages.length === 0 ? (
              <p className="pc-record-quiet">
                {gated ? "Confirm your profile to start a conversation with your coach." : "Ask about a concept, your record, or something you want to practice."}
              </p>
            ) : null}
            {coachMessages.map((m, i) => (
              <div key={`${String(i)}-${m.text.slice(0, 12)}`}>
                <p className={m.from === "you" ? "pcs-said" : "pc-record-coach-prose"}>{m.text}</p>
                {m.from === "coach" && (m.references?.length ?? 0) > 0 ? <EvidenceLinks references={m.references ?? []} onView={onViewMoment} /> : null}
              </div>
            ))}
          </div>
          <div className="pcs-composer">
            <FreeTextInput
              placeholder="Ask about the record."
              disabled={coachBusy || gated}
              disabledReason={gated ? "Finish a case first." : coachBusy ? "Reading the record." : undefined}
              onSubmit={onSendCoach}
              onOpenSettings={onOpenSettings}
              compact
            />
          </div>
        </section>
      </div>
    </main>
  );
}
